import { ref, computed } from 'vue';
import pageWindowNavigationService from '@/utils/pageWindowNavigation.service';
import { getIconFromRoute, getIconForWindow } from '@/utils/pageWindowIcons';

const windows = ref([]);
const activeWindowId = ref(null);
const switcherOpen = ref(false);

/** Reactive mirror of pageWindowNavigationService — localStorage alone is not reactive in Vue. */
const pageWindowMode = ref(pageWindowNavigationService.getCurrentMode());

function syncPageWindowModeFromEvent(event) {
  const mode = event?.detail?.mode;
  if (mode && pageWindowNavigationService.isValidMode(mode)) {
    pageWindowMode.value = mode;
    return;
  }
  pageWindowMode.value = pageWindowNavigationService.getCurrentMode();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pageWindowModeChanged', syncPageWindowModeFromEvent);
}

function isMultiWindowActive() {
  return pageWindowMode.value === 'multi';
}

function syncOpenPathsToService() {
  pageWindowNavigationService.setOpenWindowPaths(
    windows.value.map((w) => w.fullPath)
  );
}

const ROUTE_NAME_LABELS = {
  CustomerCenter: 'Customer Center',
  CustomersList: 'Customers',
  Invoices: 'Customer Invoices',
  CustomersItems: 'Customer Items',
  CustomerRefund: 'Customer Refund',
  ReceivePayment: 'Receive Payment',
  CustomerPrepayment: 'Customer Prepayment',
  NewCustomer: 'New Customer',
  EditCustomer: 'Edit Customer',
  CustomerAddNewInvoice: 'New Invoice',
  CustomerCreateInvoice: 'Create Invoice',
  CustomerNewCreditMemo: 'Credit Memo',
  NewCashSale: 'Cash Sale',
  VendorCenter: 'Vendor Center',
  VendorsList: 'Vendors',
  EnterBill: 'Enter Bill',
  CreateBill: 'Create Bill',
  DraftBills: 'Drafts & Captured',
  VendorPortalHub: 'Invites & Submitted',
  VendorMailSources: 'Bill Mail Setup',
  PayBill: 'Pay Bill',
  VendorBills: 'Vendor Bills',
  VendorPayments: 'Vendor Payments',
  CompanyDashboard: 'Dashboard',
  CompanyOverview: 'Overview',
  TransactionsCenter: 'Transactions',
  CompanyPreferences: 'Company Preferences',
  UIPreferences: 'UI Preferences',
  SessionAlertSettings: 'Session Alert',
  SettingsDataExport: 'Data Export',
  SettingsDataImport: 'Data Import',
  Users: 'Users',
  ReportsHome: 'Reports Home',
  Tracker: 'Tracker',
};

function generateId() {
  return `win_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function humanizeSegment(segment) {
  if (!segment) return '';
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const stagedBatchFileNames = {};

function getStagedBatchIdFromPath(fullPath) {
  const match = String(fullPath || '').match(/[?&]stagedBatchId=([^&#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function getStagedBatchTitle(batchId) {
  return stagedBatchFileNames[String(batchId)] || 'Staged Payroll';
}

export function setStagedBatchFileName(batchId, fileName) {
  if (!batchId) return;
  const key = String(batchId);
  const name = String(fileName || '').trim();
  if (name) {
    stagedBatchFileNames[key] = name;
  } else {
    delete stagedBatchFileNames[key];
  }
  windows.value.forEach((win) => {
    if (getStagedBatchIdFromPath(win.fullPath) === key) {
      win.title = getStagedBatchTitle(key);
    }
  });
}

export function getTitleFromRoute(route) {
  if (!route) return 'Page';

  if (route.name === 'RecordPayroll' && route.query?.stagedBatchId) {
    return getStagedBatchTitle(route.query.stagedBatchId);
  }

  if (route.name && ROUTE_NAME_LABELS[route.name]) {
    return ROUTE_NAME_LABELS[route.name];
  }

  if (route.name && typeof route.name === 'string') {
    const spaced = route.name.replace(/([A-Z])/g, ' $1').trim();
    if (spaced) return spaced;
  }

  const parts = (route.path || '').split('/').filter(Boolean);
  const dashboardIdx = parts.indexOf('dashboard');
  if (dashboardIdx !== -1 && parts.length > dashboardIdx + 1) {
    const segments = parts.slice(dashboardIdx + 1);
    if (segments.length === 1) {
      return humanizeSegment(segments[0]);
    }
    return segments.map(humanizeSegment).join(' › ');
  }

  const last = parts[parts.length - 1];
  return humanizeSegment(last) || 'Page';
}

function getCompanyIdFromPath(pathOrFullPath) {
  if (!pathOrFullPath) return null;
  const match = String(pathOrFullPath).match(/\/company\/([^/?#]+)/);
  return match ? match[1] : null;
}

function pruneWindowsForCompany(companyId) {
  if (!companyId) {
    return;
  }
  const targetId = String(companyId);
  const kept = windows.value.filter((win) => {
    const winCompanyId = getCompanyIdFromPath(win.fullPath || win.path);
    // Keep only windows for the active company
    return winCompanyId && String(winCompanyId) === targetId;
  });

  if (kept.length === windows.value.length) {
    return;
  }

  windows.value = kept;
  if (!windows.value.some((w) => w.id === activeWindowId.value)) {
    activeWindowId.value = windows.value[0]?.id || null;
  }
  syncOpenPathsToService();
}

function openFromRoute(route) {
  if (!isMultiWindowActive()) return;
  if (!route || !route.fullPath) return;

  const companyId = route.params?.id || getCompanyIdFromPath(route.fullPath || route.path);
  if (companyId) {
    pruneWindowsForCompany(companyId);
  }

  const fullPath = route.fullPath;
  const existingExact = windows.value.find((w) => w.fullPath === fullPath);
  if (existingExact) {
    activeWindowId.value = existingExact.id;
    if (!existingExact.icon) {
      existingExact.icon = getIconFromRoute(route);
    }
    syncOpenPathsToService();
    return;
  }

  // Same page path with different query (filters, view mode, etc.) — update the
  // existing window instead of opening a duplicate tab.
  const pathOnly = pathWithoutQuery(fullPath);
  const existingSamePath = windows.value.find(
    (w) => pathWithoutQuery(w.fullPath || w.path) === pathOnly
  );
  if (existingSamePath) {
    existingSamePath.fullPath = fullPath;
    existingSamePath.path = route.path || pathOnly;
    existingSamePath.title = getTitleFromRoute(route);
    existingSamePath.icon = getIconFromRoute(route);
    activeWindowId.value = existingSamePath.id;
    syncOpenPathsToService();
    return;
  }

  const win = {
    id: generateId(),
    fullPath,
    title: getTitleFromRoute(route),
    path: route.path,
    icon: getIconFromRoute(route),
    refreshKey: 0,
  };
  windows.value.push(win);
  activeWindowId.value = win.id;
  syncOpenPathsToService();
}

function pathWithoutQuery(fullPath) {
  return String(fullPath || '').split('?')[0];
}

function shouldClearAddFormQueryOnRefresh(fullPath) {
  const path = pathWithoutQuery(fullPath);
  return /\/organization-loans\/add$/.test(path) && String(fullPath || '').includes('?');
}

function refreshActiveWindow(router) {
  if (!isMultiWindowActive()) return false;
  const win = windows.value.find((w) => w.id === activeWindowId.value);
  if (!win) return false;

  win.refreshKey = (win.refreshKey || 0) + 1;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pageWindowRefresh', {
      detail: {
        windowId: win.id,
        fullPath: win.fullPath,
        refreshKey: win.refreshKey,
      },
    }));
  }

  if (shouldClearAddFormQueryOnRefresh(win.fullPath)) {
    const pathOnly = pathWithoutQuery(win.fullPath);
    win.fullPath = pathOnly;
    win.path = pathOnly;
    if (router && router.currentRoute?.value?.fullPath !== pathOnly) {
      router.replace(pathOnly).catch(() => {});
    }
  }

  return true;
}

function getRefreshKeyForPath(fullPath) {
  const win = windows.value.find((w) => w.fullPath === fullPath);
  return win?.refreshKey || 0;
}

function switchToWindow(id, router) {
  const win = windows.value.find((w) => w.id === id);
  if (!win || !router) return;

  const currentCompanyId = router.currentRoute?.value?.params?.id;
  const winCompanyId = getCompanyIdFromPath(win.fullPath || win.path);
  if (
    currentCompanyId &&
    winCompanyId &&
    String(currentCompanyId) !== String(winCompanyId)
  ) {
    // Stale tab from another company — drop foreign windows and stay on current company
    pruneWindowsForCompany(currentCompanyId);
    return;
  }

  activeWindowId.value = id;
  if (router.currentRoute.value.fullPath !== win.fullPath) {
    router.push(win.fullPath);
  }
}

function closeWindow(id, router) {
  const idx = windows.value.findIndex((w) => w.id === id);
  if (idx === -1) return;

  const wasActive = activeWindowId.value === id;
  windows.value.splice(idx, 1);
  syncOpenPathsToService();

  if (windows.value.length === 0) {
    activeWindowId.value = null;
    const companyId = router?.currentRoute?.value?.params?.id;
    if (companyId && router) {
      router.push(`/company/${companyId}/dashboard/overview`);
    }
    return;
  }

  if (wasActive) {
    const next = windows.value[Math.min(idx, windows.value.length - 1)];
    switchToWindow(next.id, router);
  }
}

function closeActiveWindow(router) {
  if (!isMultiWindowActive()) return false;
  if (!activeWindowId.value) return false;
  closeWindow(activeWindowId.value, router);
  return true;
}

function cycleWindow(direction, router) {
  if (!isMultiWindowActive()) return;
  if (windows.value.length < 2) return;

  const idx = windows.value.findIndex((w) => w.id === activeWindowId.value);
  const currentIdx = idx === -1 ? 0 : idx;
  const nextIdx = (currentIdx + direction + windows.value.length) % windows.value.length;
  switchToWindow(windows.value[nextIdx].id, router);
}

function openSwitcher() {
  if (!isMultiWindowActive()) return;
  if (windows.value.length === 0) return;
  switcherOpen.value = true;
}

function closeSwitcher() {
  switcherOpen.value = false;
}

function clearWindows() {
  windows.value = [];
  activeWindowId.value = null;
  syncOpenPathsToService();
}

export function usePageWindows() {
  const isMultiWindowEnabled = computed(() => pageWindowMode.value === 'multi');
  const openWindows = computed(() => windows.value);
  const activeWindow = computed(() =>
    windows.value.find((w) => w.id === activeWindowId.value) || null
  );

  return {
    openWindows,
    activeWindow,
    activeWindowId,
    switcherOpen,
    isMultiWindowEnabled,
    pageWindowMode,
    openFromRoute,
    switchToWindow,
    closeWindow,
    closeActiveWindow,
    cycleWindow,
    openSwitcher,
    closeSwitcher,
    clearWindows,
    pruneWindowsForCompany,
    refreshActiveWindow,
    getRefreshKeyForPath,
    getTitleFromRoute,
    getIconForWindow,
  };
}
